import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { ChampionshipListItem } from 'modules/championship/domain/interfaces/championship-list-item';
import { ListChampionshipsArgs, useListChampionshipsQuery } from 'modules/championship/store/championships-api';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { usePagedList } from 'shared/hooks/paged-list';

/** Ключ localStorage для `perPage`: размер страницы запоминается между визитами. */
const STORAGE_KEY = 'championships';
/** Карточки идут сеткой по три в ряд — девятка даёт ровно три ряда. */
const DEFAULT_PER_PAGE = 9;

/**
 * Стабильная ссылка обязательна: `usePagedList` считает по аргументам ключ скоупа,
 * а безымянная стрелка меняла бы идентичность на каждом рендере.
 */
const mapSearchToArgs = (search: string): Partial<ListChampionshipsArgs> => ({ namePart: search || undefined });

/**
 * Список соревнований организации: страница, размер страницы и поиск живут в URL,
 * данные — в RTK Query.
 *
 * Раньше нумерация страниц считалась `use-block-pager-core` и хранилась в
 * module-level `Map` (`speculative-pages.ts`): состояние переживало unmount, и
 * список открывался с числом страниц от прошлого визита. Теперь оно живёт в
 * `usePagedList` вместе с остальным состоянием списка.
 */
export const useChampionshipsList = () => {
  const tournamentId = useSelector(authStoreUserTournamentId);

  const baseArgs = useMemo(() => ({ tournamentId: Number(tournamentId) }), [tournamentId]);

  return usePagedList<ChampionshipListItem, ListChampionshipsArgs>({
    useQueryHook: useListChampionshipsQuery,
    baseArgs,
    mapSearchToArgs,
    storageKey: STORAGE_KEY,
    defaultPerPage: DEFAULT_PER_PAGE,
    // Пока организация неизвестна, запрос ушёл бы за чужими турнирами.
    skip: !tournamentId,
    isPending: !tournamentId,
  });
};
